<template>
  <Teleport to="body">
    <Transition name="trial-dialog">
      <div
        v-if="isOpen"
        ref="dialogLayer"
        class="trial-dialog-layer"
        role="presentation"
        @mousedown.self="closeDialog"
      >
        <section
          ref="dialogPanel"
          class="trial-dialog-panel"
          :class="{ 'trial-dialog-panel-dark': colorMode.value === 'dark' }"
          role="dialog"
          aria-modal="true"
          aria-labelledby="trial-dialog-title"
          aria-describedby="trial-dialog-description trial-dialog-terms trial-dialog-consent"
          @mousedown.stop
        >
          <button
            ref="closeButton"
            type="button"
            class="trial-dialog-close"
            :aria-label="copy.close"
            @click="closeDialog"
          >
            <span aria-hidden="true">&times;</span>
          </button>

          <div class="trial-dialog-intro">
            <h2 id="trial-dialog-title">{{ copy.title }}</h2>
            <p id="trial-dialog-description" class="trial-dialog-summary">{{ copy.summary }}</p>

            <div class="trial-dialog-proof" :aria-label="copy.detailsLabel">
              <div>
                <span>01</span>
                <p><strong>{{ copy.cancelTitle }}</strong><span class="trial-dialog-proof-note">{{ copy.cancelNote }}</span></p>
              </div>
              <div>
                <span>02</span>
                <p><strong>{{ copy.timeTitle }}</strong><span class="trial-dialog-proof-note">{{ copy.timeNote }}</span></p>
              </div>
            </div>
          </div>

          <form class="trial-dialog-form" novalidate @submit.prevent="submitLead">
            <div class="trial-dialog-form-head">
              <p>{{ copy.formTitle }}</p>
              <span v-if="selectedPlan">{{ selectedPlanLabel }}</span>
              <span v-else>{{ copy.formNext }}</span>
              <strong v-if="foundingOfferApplies" class="trial-dialog-offer">{{ foundingOfferLabel }}</strong>
            </div>

            <label for="trial-email">{{ copy.emailLabel }}</label>
            <input
              id="trial-email"
              ref="emailInput"
              v-model.trim="email"
              name="email"
              type="email"
              inputmode="email"
              autocomplete="email"
              placeholder="you@yourbusiness.com"
              :aria-invalid="Boolean(errorMessage)"
              :aria-describedby="errorMessage ? 'trial-email-error' : undefined"
              @input="errorMessage = ''"
            />

            <div class="trial-dialog-honeypot" aria-hidden="true">
              <label for="trial-company-website">Company website</label>
              <input
                id="trial-company-website"
                v-model="companyWebsite"
                name="company_website"
                type="text"
                tabindex="-1"
                autocomplete="off"
              />
            </div>

            <p
              v-if="errorMessage"
              id="trial-email-error"
              class="trial-dialog-error"
              role="alert"
            >
              {{ errorMessage }}
            </p>

            <p id="trial-dialog-terms" class="trial-dialog-terms">{{ triggerTerms || trialTermsLabel }}</p>

            <button ref="submitButton" type="submit" class="trial-dialog-submit" :disabled="isSubmitting">
              <span>{{ isSubmitting ? copy.submitting : copy.submit }}</span>
              <span aria-hidden="true">&rarr;</span>
            </button>

            <p id="trial-dialog-consent" class="trial-dialog-consent">{{ copy.consentBefore }}<button
                type="button"
                class="trial-dialog-consent-link"
                data-privacy-choices-trigger
                @click="openPreferences"
              >{{ copy.privacyChoices }}</button>{{ copy.consentAfter }}
              <NuxtLink :to="localePath('/privacy-policy')">{{ copy.privacyPolicy }}</NuxtLink>
            </p>
          </form>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';
import { campaignParams, signupCampaignUrl } from '~/utils/tracking/adLinks';
import { GOOGLE_ADS_SIGNUP_LEAD } from '~/utils/tracking/googleAds';
import pricing from '~/data/pricing-features.json';
import { FOUNDING_OFFER } from '~/data/founding-offer';
import {
  englishTrialDialogCopy,
  formatTrialDialogCopy,
  loadTrialDialogCopy,
  type TrialDialogCopy,
} from '~/data/trial-dialog-copy';

type StartFreeElement = HTMLAnchorElement & {
  dataset: DOMStringMap & {
    conversionLabel?: string;
    conversionLocation?: string;
    conversionPlan?: string;
    trialBilling?: string;
  };
};

const config = useRuntimeConfig();
const { current: currentAttributionState, leadTrackingFields } = useAttribution();
const { sessionTouchField, leadUtm } = useSessionTouch();
const { track } = useMetaPixel();
const { consent, adConsent, openPreferences, preferencesOpen } = useCookieConsent();
const route = useRoute();
const colorMode = useColorMode();
// The dialog is never prerendered, so its copy can follow the URL's locale (see
// data/trial-dialog-copy.ts); English shows until the locale chunk arrives.
const { currentLocaleCode, localePath } = useWorkspaceLocale();
const copy = shallowRef<TrialDialogCopy>(englishTrialDialogCopy);
const loadCopy = async (code: string) => {
  try {
    const loaded = await loadTrialDialogCopy(code);
    if (code === currentLocaleCode.value) copy.value = loaded;
  } catch {
    // Stay on English; openDialog retries once the visitor clicks.
    if (code === currentLocaleCode.value) copy.value = englishTrialDialogCopy;
  }
};

const isOpen = ref(false);
const isSubmitting = ref(false);
const email = ref('');
const companyWebsite = ref('');
const errorMessage = ref('');
const selectedPlan = ref('');
// Show the plan's display name and included seats, not the URL slug. The app's plan step opens
// with that plan emphasized (?plan=) on the card's billing period (?billing=); the visitor still
// confirms it there.
const selectedPlanLabel = computed(() => {
  const plan = pricing.plans.find((p) => p.slug === selectedPlan.value.toLowerCase());
  const name = plan?.name || selectedPlan.value;
  const template = plan && plan.seats > 1 ? copy.value.planSeats : copy.value.plan;
  return formatTrialDialogCopy(template, { plan: name, seats: plan?.seats ?? 1 });
});
// The line that makes the trial's terms visible beside the button: card required and the price
// after the 14 days (legal-drafts/trial-disclosure-copy.md B1.1). With a plan chosen it names the
// plan's monthly price; otherwise the lowest. Billing period is picked later, in the app.
// A yearly plan card (data-trial-billing="yearly") already prints its yearly terms, with any
// founding first-year price, under its button; the dialog repeats that line, so the price the
// visitor just read is the one beside "Continue".
const triggerTerms = ref('');
const trialTermsLabel = computed(() => {
  const plan = pricing.plans.find((p) => p.slug === selectedPlan.value.toLowerCase());
  return formatTrialDialogCopy(plan ? copy.value.trialTermsPlan : copy.value.trialTerms, {
    price: `$${(plan ?? pricing.plans[0]).monthly}`,
    plan: plan?.name ?? '',
  });
});
const sourceHref = ref('https://app.workspace369.com/signup');
const foundingOffer = useFoundingOffer();
// The founding banner and plan cards add promo=FOUNDING100 to their links; say so here so the
// offer the visitor clicked is still in view when they hand over their email.
const foundingOfferApplies = computed(() => {
  if (!foundingOffer.enabled || foundingOffer.expired.value) return false;
  try {
    return new URL(sourceHref.value, 'https://workspace369.com').searchParams.get('promo') === FOUNDING_OFFER.code;
  } catch {
    return false;
  }
});
const foundingOfferLabel = computed(() =>
  formatTrialDialogCopy(copy.value.foundingOffer, { months: FOUNDING_OFFER.firstYearMonths }),
);
const conversionLabel = ref('Start free');
const conversionLocation = ref('');
const emailInput = ref<HTMLInputElement | null>(null);
const submitButton = ref<HTMLButtonElement | null>(null);
const dialogPanel = ref<HTMLElement | null>(null);
const dialogLayer = ref<HTMLElement | null>(null);
const triggerElement = ref<HTMLElement | null>(null);
let previousBodyOverflow = '';
let previousRootOverflow = '';
let lastViewportHeight = 0;

// On iOS the layout viewport does not shrink when the keyboard opens, so a
// fixed inset-0 layer leaves the bottom sheet behind the keyboard. Track the
// visual viewport instead and pin the layer to the actually visible area.
// Applies to phones and coarse-pointer tablets (iPad hits the same case in
// the centered desktop layout).
const isMobileSheet = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;

const shouldTrackViewport = () =>
  typeof window !== 'undefined' &&
  (isMobileSheet() || window.matchMedia('(pointer: coarse)').matches);

const syncLayerViewport = () => {
  const layer = dialogLayer.value;
  if (!layer || !isOpen.value) return;
  if (!shouldTrackViewport() || !window.visualViewport) {
    layer.style.height = '';
    layer.style.transform = '';
    return;
  }
  const vv = window.visualViewport;
  layer.style.height = `${vv.height}px`;
  layer.style.transform = `translateY(${vv.offsetTop}px)`;
  // When the keyboard opens it leaves a short sheet: keep the email field and Continue in view, not the heading.
  if (vv.height < lastViewportHeight - 100 && document.activeElement === emailInput.value) {
    requestAnimationFrame(() => submitButton.value?.scrollIntoView({ block: 'nearest' }));
  }
  lastViewportHeight = vv.height;
};

const resetLayerViewport = () => {
  if (!dialogLayer.value) return;
  dialogLayer.value.style.height = '';
  dialogLayer.value.style.transform = '';
};

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const validEmail = (value: string) =>
  value.length <= 254 &&
  !value.includes('+') &&
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);

const currentAttribution = () => {
  return {
    source_path: `${window.location.pathname}${window.location.search}`.slice(0, 500),
    conversion_label: conversionLabel.value,
    conversion_location: conversionLocation.value || window.location.pathname,
    selected_plan: selectedPlan.value,
    referral_id: new URL(sourceHref.value, window.location.origin).searchParams.get('ref') || '',
    locale: document.documentElement.lang || 'en',
    ...leadUtm(),
    ...sessionTouchField(),
  };
};

const signupDestination = (leadId?: string) => {
  const clickedUrl = new URL(sourceHref.value, window.location.origin);
  const destination = new URL('/signup', 'https://app.workspace369.com');
  clickedUrl.searchParams.forEach((value, key) => destination.searchParams.set(key, value));
  destination.searchParams.set('email', normalizeEmail(email.value));
  if (leadId) destination.searchParams.set('lead_id', leadId);
  destination.searchParams.set('source', 'landing');
  destination.searchParams.set('page', 'signup');
  // Same consented campaign handoff as direct app links, including YouTube.
  const state = currentAttributionState();
  const adParams = campaignParams(
    window.location.search,
    state?.last,
    state?.last_meta,
    adConsent.value === 'granted',
  );
  return signupCampaignUrl(destination.toString(), adParams, adConsent.value === 'granted');
};

const emitCapturedEvent = (leadId: string) => {
  const payload = {
    event_category: 'conversion',
    event_label: conversionLabel.value,
    location: conversionLocation.value,
    plan: selectedPlan.value || undefined,
    lead_id: leadId.slice(0, 12),
    page_path: window.location.pathname,
  };

  if (typeof window.gtag === 'function') {
    const deliver = (event: string, parameters: Record<string, unknown>) =>
      new Promise<void>((resolve) => {
        const timer = window.setTimeout(resolve, 1000);
        window.gtag?.('event', event, {
          ...parameters,
          event_timeout: 1000,
          event_callback: () => { window.clearTimeout(timer); resolve(); },
        });
      });
    const pending: Promise<void>[] = [];
    if (consent.value?.analytics) {
      pending.push(deliver('trial_lead_captured', payload));
    } else {
      window.gtag('event', 'trial_lead_captured', payload);
    }
    // Google Ads "Sign-up lead" conversion; its tag only loads with ad consent, so only send then.
    if (adConsent.value === 'granted') {
      pending.push(deliver('conversion', { send_to: GOOGLE_ADS_SIGNUP_LEAD }));
    }
    return Promise.all(pending);
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'trial_lead_captured', ...payload });
};

const openDialog = async (element: StartFreeElement) => {
  if (currentLocaleCode.value !== 'en' && copy.value === englishTrialDialogCopy) {
    void loadCopy(currentLocaleCode.value);
  }
  triggerElement.value = element;
  sourceHref.value = element.href || 'https://app.workspace369.com/signup';
  conversionLabel.value = element.dataset.conversionLabel || element.textContent?.trim() || 'Start free';
  conversionLocation.value = element.dataset.conversionLocation || route.path;
  selectedPlan.value =
    element.dataset.conversionPlan ||
    new URL(sourceHref.value, window.location.origin).searchParams.get('plan') ||
    '';
  triggerTerms.value =
    element.dataset.trialBilling === 'yearly'
      ? element.parentElement?.querySelector('[data-trial-disclosure]')?.textContent?.trim() || ''
      : '';
  errorMessage.value = '';
  companyWebsite.value = '';
  isOpen.value = true;
  previousBodyOverflow = document.body.style.overflow;
  previousRootOverflow = document.documentElement.style.overflow;
  document.body.style.overflow = 'hidden';
  // iOS Safari still scrolls the page behind when only <body> is hidden; lock the root too.
  document.documentElement.style.overflow = 'hidden';
  await nextTick();
  syncLayerViewport();
  // Mobile/tablet: let the user tap the field themselves so the keyboard
  // opens only after the sheet has settled; desktop keeps autofocus.
  if (!shouldTrackViewport()) emailInput.value?.focus();
};

const closeDialog = () => {
  if (isSubmitting.value) return;
  isOpen.value = false;
  resetLayerViewport();
  document.body.style.overflow = previousBodyOverflow;
  document.documentElement.style.overflow = previousRootOverflow;
  nextTick(() => triggerElement.value?.focus());
};

const handleDocumentClick = (event: MouseEvent) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target instanceof Element ? event.target : null;
  const element = target?.closest('[data-conversion-event="start_free_click"]');
  if (!(element instanceof HTMLAnchorElement)) return;
  event.preventDefault();
  void openDialog(element as StartFreeElement);
};

const handleKeydown = (event: KeyboardEvent) => {
  // The cookie preferences dialog stacks above this one (see CookieConsent.vue)
  // and owns its own Escape/Tab handling — stay out of its way entirely while open.
  if (preferencesOpen.value) return;
  if (!isOpen.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closeDialog();
    return;
  }
  if (event.key !== 'Tab' || !dialogPanel.value) return;
  const focusable = Array.from(
    dialogPanel.value.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
    ),
  );
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const submitLead = async () => {
  const normalized = normalizeEmail(email.value);
  if (!validEmail(normalized)) {
    errorMessage.value = normalized.includes('+')
      ? copy.value.errorPlus
      : copy.value.errorInvalid;
    emailInput.value?.focus();
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';
  let response: Response | null = null;
  let body: { error?: string; lead_id?: string; event_id?: string } | null = null;
  try {
    response = await fetch(config.public.trialLeadEndpoint as string, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: normalized,
        company_website: companyWebsite.value,
        ...currentAttribution(),
        ...leadTrackingFields(),
      }),
    });
    body = (await response.json().catch(() => ({}))) as typeof body;
  } catch {
    // Blocked (an ad blocker or privacy extension) or offline: the lead is not
    // saved, but sign-up doesn't need it, so the visitor still goes on below.
  }

  // Only a refused address stops the visitor here: they have to correct it.
  if (response?.status === 400) {
    errorMessage.value = body?.error || copy.value.errorSave;
    isSubmitting.value = false;
    await nextTick();
    emailInput.value?.focus();
    return;
  }

  // Any other failure (blocked, offline, rate limited, server error) continues
  // to sign-up with the email filled in, just without a lead id.
  const leadId = response?.ok ? body?.lead_id : undefined;
  if (leadId) {
    try {
      const googleDelivery = emitCapturedEvent(leadId);
      let pixelDelivery = Promise.resolve();
      if (body?.event_id && track('Lead', { content_name: 'trial', content_category: selectedPlan.value || 'none' }, body.event_id)) {
        // Give the Pixel request a moment to leave before navigating away.
        pixelDelivery = new Promise((resolve) => window.setTimeout(resolve, 300));
      }
      // Worker delivery is asynchronous; allow the conversion to leave before
      // unloading, but never let a blocked analytics request hold up signup.
      await Promise.all([googleDelivery, pixelDelivery]);
    } catch {
      // Analytics never holds up sign-up.
    }
  }
  window.location.assign(signupDestination(leadId));
};

watch(currentLocaleCode, (code) => {
  void loadCopy(code);
});

onMounted(() => {
  void loadCopy(currentLocaleCode.value);
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKeydown);
  window.visualViewport?.addEventListener('resize', syncLayerViewport);
  window.visualViewport?.addEventListener('scroll', syncLayerViewport);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKeydown);
  window.visualViewport?.removeEventListener('resize', syncLayerViewport);
  window.visualViewport?.removeEventListener('scroll', syncLayerViewport);
  if (isOpen.value) {
    document.body.style.overflow = previousBodyOverflow;
    document.documentElement.style.overflow = previousRootOverflow;
  }
});
</script>

<style scoped>
.trial-dialog-layer {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgb(2 6 23 / 74%);
  backdrop-filter: blur(14px);
}

.trial-dialog-panel {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(360px, 0.95fr);
  width: min(920px, 100%);
  max-height: min(720px, calc(100% - 48px));
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  border: 1px solid rgb(203 213 225 / 80%);
  border-radius: 8px;
  background: #fff;
  color: #020617;
  box-shadow: 0 32px 100px rgb(2 6 23 / 42%);
}

.trial-dialog-close {
  position: absolute;
  top: 18px;
  inset-inline-end: 18px;
  z-index: 2;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  color: #334155;
  font-size: 25px;
  line-height: 1;
  transition: border-color 150ms ease, color 150ms ease, transform 150ms ease;
}

/* 44px tap area around the 36px circle. */
.trial-dialog-close::after {
  content: '';
  position: absolute;
  inset: -4px;
}

.trial-dialog-close:hover {
  border-color: #0891b2;
  color: #0e7490;
  transform: rotate(4deg);
}

.trial-dialog-intro,
.trial-dialog-form {
  padding: 56px 48px 48px;
}

.trial-dialog-intro {
  border-inline-end: 1px solid #e2e8f0;
  background: #f8fafc;
}

.trial-dialog-intro h2 {
  max-width: 450px;
  margin: 0;
  color: #020617;
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: 0;
}

.trial-dialog-summary {
  max-width: 470px;
  margin: 22px 0 0;
  color: #475569;
  font-size: 16px;
  line-height: 1.7;
}

.trial-dialog-proof {
  display: grid;
  gap: 0;
  margin-top: 36px;
  border-top: 1px solid #cbd5e1;
}

.trial-dialog-proof > div {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 12px;
  padding: 18px 0;
  border-bottom: 1px solid #cbd5e1;
}

.trial-dialog-proof > div > span {
  color: #0891b2;
  font-size: 12px;
  font-weight: 700;
}

.trial-dialog-proof p,
.trial-dialog-proof strong {
  display: block;
  margin: 0;
}

.trial-dialog-proof p {
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.trial-dialog-proof-note {
  display: block;
}

.trial-dialog-proof strong {
  margin-bottom: 3px;
  color: #0f172a;
  font-size: 14px;
}

.trial-dialog-form {
  align-self: center;
  padding-top: 72px;
}

.trial-dialog-form-head {
  margin-bottom: 28px;
}

.trial-dialog-form-head p,
.trial-dialog-form-head span {
  display: block;
  margin: 0;
}

.trial-dialog-form-head p {
  color: #0f172a;
  font-size: 21px;
  font-weight: 700;
}

.trial-dialog-offer {
  display: inline-block;
  margin-top: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  background: #ecfeff;
  color: #0e7490;
  font-size: 13px;
  font-weight: 700;
}

.trial-dialog-panel-dark .trial-dialog-offer {
  background: rgb(8 145 178 / 18%);
  color: #67e8f9;
}

.trial-dialog-form-head span {
  margin-top: 5px;
  color: #64748b;
  font-size: 13px;
}

.trial-dialog-form label {
  display: block;
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.trial-dialog-form input[type='email'] {
  width: 100%;
  height: 54px;
  padding: 0 16px;
  border: 1px solid #94a3b8;
  border-radius: 6px;
  background: #fff;
  color: #020617;
  font-size: 16px;
  outline: none;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.trial-dialog-form input[type='email']:focus {
  border-color: #0891b2;
  box-shadow: 0 0 0 3px rgb(6 182 212 / 14%);
}

.trial-dialog-form input[aria-invalid='true'] {
  border-color: #dc2626;
}

.trial-dialog-submit {
  display: flex;
  width: 100%;
  min-height: 54px;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 0 18px;
  border: 1px solid #0e7490;
  border-radius: 6px;
  background: #0e7490;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  transition: background 150ms ease, transform 150ms ease;
}

/* The arrow points the way the text reads (left in Arabic and Hebrew). */
.trial-dialog-submit span[aria-hidden='true']:dir(rtl) {
  transform: scaleX(-1);
}

.trial-dialog-submit:hover:not(:disabled) {
  background: #155e75;
  transform: translateY(-1px);
}

.trial-dialog-submit:disabled {
  cursor: wait;
  opacity: 0.72;
}

.trial-dialog-error {
  margin: 10px 0 -4px;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.5;
}

.trial-dialog-terms {
  margin: 14px 0 0;
  color: #334155;
  font-size: 13px;
  line-height: 1.5;
}


.trial-dialog-panel-dark .trial-dialog-terms {
  color: #cbd5e1;
}

.trial-dialog-consent {
  margin: 18px 0 0;
  color: #64748b;
  font-size: 11px;
  line-height: 1.65;
}

.trial-dialog-consent a {
  display: inline-block;
  margin-inline-start: 4px;
  color: #0e7490;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.trial-dialog-consent-link {
  border: 0;
  margin: 0;
  padding: 0;
  background: none;
  color: #0e7490;
  font: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.trial-dialog-honeypot {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.trial-dialog-enter-active,
.trial-dialog-leave-active {
  transition: opacity 180ms ease;
}

.trial-dialog-enter-active .trial-dialog-panel,
.trial-dialog-leave-active .trial-dialog-panel {
  transition: opacity 180ms ease, transform 180ms ease;
}

.trial-dialog-enter-from,
.trial-dialog-leave-to {
  opacity: 0;
}

.trial-dialog-enter-from .trial-dialog-panel,
.trial-dialog-leave-to .trial-dialog-panel {
  opacity: 0;
  transform: translateY(12px) scale(0.99);
}

.trial-dialog-panel-dark {
  border-color: #334155;
  background: #07101f;
  color: #f8fafc;
}

.trial-dialog-panel-dark .trial-dialog-intro {
  border-color: #243247;
  background: #0b1526;
}

.trial-dialog-panel-dark .trial-dialog-intro h2,
.trial-dialog-panel-dark .trial-dialog-form-head p,
.trial-dialog-panel-dark .trial-dialog-proof strong {
  color: #f8fafc;
}

.trial-dialog-panel-dark .trial-dialog-summary,
.trial-dialog-panel-dark .trial-dialog-form-head span,
.trial-dialog-panel-dark .trial-dialog-consent,
.trial-dialog-panel-dark .trial-dialog-proof p {
  color: #a9b7cb;
}

.trial-dialog-panel-dark .trial-dialog-proof,
.trial-dialog-panel-dark .trial-dialog-proof > div {
  border-color: #334155;
}

.trial-dialog-panel-dark .trial-dialog-form label {
  color: #cbd5e1;
}

.trial-dialog-panel-dark .trial-dialog-form input[type='email'] {
  border-color: #475569;
  background: #0f1a2d;
  color: #fff;
}

.trial-dialog-panel-dark .trial-dialog-close {
  border-color: #475569;
  background: #0f1a2d;
  color: #cbd5e1;
}

@media (max-width: 760px) {
  .trial-dialog-layer {
    align-items: end;
    padding: 0;
  }

  .trial-dialog-panel {
    grid-template-columns: 1fr;
    width: 100%;
    max-height: calc(100% - 16px);
    border-right: 0;
    border-bottom: 0;
    border-left: 0;
    border-radius: 8px 8px 0 0;
  }

  .trial-dialog-intro {
    padding: 34px 24px 24px;
    border-inline-end: 0;
    border-bottom: 1px solid #e2e8f0;
  }

  .trial-dialog-intro h2 {
    /* Keeps the title clear of the close button, on the right or (RTL) left. */
    padding-inline-end: 38px;
    font-size: 30px;
  }

  .trial-dialog-summary {
    margin-top: 14px;
    font-size: 14px;
    line-height: 1.6;
  }

  .trial-dialog-summary {
    display: none;
  }

  /* Phones keep the two trial facts as one compact row under the heading. */
  .trial-dialog-proof {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 18px;
    margin-top: 14px;
    border-top: 0;
  }

  .trial-dialog-proof > div {
    display: block;
    padding: 0;
    border-bottom: 0;
  }

  .trial-dialog-proof > div > span,
  .trial-dialog-proof-note {
    display: none;
  }

  .trial-dialog-proof strong {
    margin: 0;
    font-size: 14px;
  }

  .trial-dialog-form {
    padding: 24px;
  }

  .trial-dialog-form-head {
    margin-bottom: 18px;
  }

  .trial-dialog-close {
    top: 14px;
    inset-inline-end: 14px;
  }
}

/* Short phones (iPhone SE/mini, landscape, larger text): Safari's toolbars leave ~550px, and the sheet put
   Continue below the bottom edge. Tighten the heading block so the email field and Continue fit. */
@media (max-width: 760px) and (max-height: 760px) {
  .trial-dialog-intro {
    padding: 20px 20px 14px;
  }

  .trial-dialog-intro h2 {
    font-size: 24px;
  }

  .trial-dialog-proof {
    margin-top: 8px;
  }

  .trial-dialog-form {
    padding: 16px 20px 20px;
  }

  .trial-dialog-form-head {
    margin-bottom: 12px;
  }

  .trial-dialog-form-head p {
    font-size: 18px;
  }

  .trial-dialog-offer {
    margin-top: 8px;
  }

  .trial-dialog-terms {
    margin-top: 10px;
  }

  .trial-dialog-submit {
    margin-top: 12px;
  }

  .trial-dialog-close {
    top: 12px;
  }
}

/* Phones on their side: there is no room for the heading block, and the form's own heading, plan and trial terms
   say the same. Show only the form, so the email field and Continue fit above the fold. */
@media (max-height: 480px) {
  .trial-dialog-layer {
    padding: 8px;
  }

  .trial-dialog-panel {
    grid-template-columns: 1fr;
    width: min(560px, 100%);
    max-height: calc(100% - 8px);
    border-radius: 8px;
  }

  .trial-dialog-intro {
    display: none;
  }

  .trial-dialog-form {
    padding: 14px 20px 16px;
  }

  .trial-dialog-form-head {
    margin-bottom: 10px;
    padding-inline-end: 44px;
  }

  .trial-dialog-form-head p {
    font-size: 18px;
  }

  .trial-dialog-form input[type='email'] {
    height: 48px;
  }

  .trial-dialog-submit {
    min-height: 48px;
    margin-top: 10px;
  }

  .trial-dialog-terms {
    margin-top: 8px;
  }

  .trial-dialog-close {
    top: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .trial-dialog-enter-active,
  .trial-dialog-leave-active,
  .trial-dialog-enter-active .trial-dialog-panel,
  .trial-dialog-leave-active .trial-dialog-panel {
    transition: none;
  }
}
</style>
